import React, { useCallback, useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import Button from '@atlaskit/button';
import { Field } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { AsyncSelect } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import debouncePromise from '@atlassian/jira-platform-debounce-promise';

import { DEBOUNCE_DELAY } from '../../../../common/constants';
import type { ProjectStyle } from '../../../../common/constants/project';
import type { ThirdPartyEntity } from '../../../../common/types';
import { customComponentsWithMenuList } from '../../../../common/ui/custom-select-components';
import {
	type OptionType,
	convertEntityToOption,
	getSelectedAndAllOptions,
} from '../../../../common/ui/custom-select-components/utils';

import messages from './messages';

export interface Props {
	label: string;
	placeholder: string;
	starredOptionsLabel: string;
	otherOptionsLabel: string;
	noEntitiesFoundHeader: string;
	noEntitiesFoundDescription: string;
	errorOccurredHeader: string;
	searchErrorOccurredDescription: string;
	loadingMoreErrorOccurredDescription: string;
	onRetry: () => void;
	entities: ThirdPartyEntity[] | null;
	selectedEntityId: string | null;
	isLoading: boolean;
	isDisabled?: boolean;
	isRequired?: boolean;
	autoFocus?: boolean;
	openMenuOnFocus?: boolean;
	hasErrorOccurred: boolean;
	hasSearchErrorOccurred: boolean;
	onChange: (entityId: string, entityName: string, style?: ProjectStyle) => void;
	placeholderColor?: string;
	onSearchEntities: (query: string) => Promise<ThirdPartyEntity[] | null>;
	onResetSearchError: () => void;
	selectedEntityName: string | null;
	onMenuScrollToBottom: () => void;
}

export const EntitySelectorWithPagination = ({
	label,
	placeholder,
	entities,
	selectedEntityId,
	isLoading,
	isDisabled = false,
	isRequired,
	autoFocus = false,
	openMenuOnFocus = false,
	onChange,
	onSearchEntities,
	onResetSearchError,
	hasErrorOccurred,
	hasSearchErrorOccurred,
	starredOptionsLabel,
	otherOptionsLabel,
	noEntitiesFoundHeader,
	noEntitiesFoundDescription,
	errorOccurredHeader,
	searchErrorOccurredDescription,
	loadingMoreErrorOccurredDescription,
	onRetry,
	placeholderColor,
	selectedEntityName,
	onMenuScrollToBottom,
}: Props) => {
	const { formatMessage } = useIntl();
	const isSearching = useRef<boolean>(false);

	const onChangeHandler = useCallback(
		(option: OptionType | null) => {
			option && onChange(option.value, option.label, option.style);
			isSearching.current = false;
		},
		[onChange],
	);

	const { selectedOption, options } = useMemo(() => {
		const entitiesWithSelected = [...(entities || [])];
		if (selectedEntityId !== null) {
			const entitiesSet = new Set(entities?.map((entity) => entity.id) || []);
			entitiesSet.add(selectedEntityId);
			if (entitiesSet.size !== entitiesWithSelected.length) {
				entitiesWithSelected.push({
					id: selectedEntityId,
					name: selectedEntityName || '',
				});
			}
		}

		return getSelectedAndAllOptions(
			entitiesWithSelected,
			selectedEntityId,
			starredOptionsLabel,
			otherOptionsLabel,
			placeholderColor,
		);
	}, [
		entities,
		otherOptionsLabel,
		placeholderColor,
		selectedEntityId,
		selectedEntityName,
		starredOptionsLabel,
	]);

	const debouncedLoadOptions = useMemo(
		() =>
			debouncePromise((inputValue: string) => {
				if (inputValue === '') {
					return Promise.resolve(options);
				}

				return onSearchEntities(inputValue).then(
					(searchResultEntities) =>
						searchResultEntities?.map((entity) =>
							convertEntityToOption({ ...entity, placeholderColor }),
						) || [],
				);
			}, DEBOUNCE_DELAY),
		[onSearchEntities, options, placeholderColor],
	);

	const onInputChangeHandler = useCallback(
		(inputValue: string) => {
			isSearching.current = !isEmpty(inputValue);
			if (!isSearching.current && hasSearchErrorOccurred) {
				onResetSearchError();
			}
		},
		[hasSearchErrorOccurred, onResetSearchError],
	);

	const onMenuScrollToBottomHandler = useCallback(() => {
		if (isSearching.current) {
			return;
		}
		onMenuScrollToBottom();
	}, [onMenuScrollToBottom]);

	if (!isLoading && entities && entities.length === 0) {
		return (
			<Box paddingBlockStart="space.200">
				<SectionMessage title={noEntitiesFoundHeader} appearance="warning">
					<p>{noEntitiesFoundDescription}</p>
				</SectionMessage>
			</Box>
		);
	}

	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.entity-content.entity-selector-with-pagination.wrapper">
			<Field label={label} name={`selector-${kebabCase(label)}`} isRequired={isRequired}>
				{({ fieldProps }) => (
					<>
						<AsyncSelect<OptionType>
							{...fieldProps}
							inputId={`entityDropdown-${kebabCase(label)}`}
							name="entityDropdown"
							defaultOptions={options}
							loadOptions={debouncedLoadOptions}
							value={selectedOption}
							onChange={onChangeHandler}
							placeholder={placeholder}
							isClearable={false}
							isLoading={isLoading}
							isDisabled={isDisabled || (hasErrorOccurred && !entities)}
							autoFocus={autoFocus}
							openMenuOnFocus={openMenuOnFocus}
							components={customComponentsWithMenuList}
							menuListProps={{
								hasSearchErrorOccurred,
								searchErrorOccurredDescription,
								isLoadingMore: isLoading && !!entities,
								hasLoadingMoreErrorOccurred: hasErrorOccurred && !!entities,
								loadingMoreErrorOccurredDescription,
								onRetryLoadingMore: onMenuScrollToBottomHandler,
							}}
							onInputChange={onInputChangeHandler}
							onMenuScrollToBottom={onMenuScrollToBottomHandler}
						/>
						{hasErrorOccurred && !entities && (
							<Box paddingBlockStart="space.200">
								<SectionMessage title={errorOccurredHeader} appearance="warning">
									<Button onClick={onRetry} appearance="link" spacing="none">
										{formatMessage(messages.tryAgain)}
									</Button>
								</SectionMessage>
							</Box>
						)}
					</>
				)}
			</Field>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	marginTop: token('space.300', '24px'),
	width: '100%',
});
